import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { freeKeyService, FreeKeyPublic } from '../../../services/freeKeyService';
import { useToast } from '../../../components/Toast';
import { getVietnamToday, dateToPeriodValue, periodValueToDate } from '../utils/dateUtils';

export type PeriodType = 'year' | 'month' | 'week' | 'day';

export const useFreeKeys = () => {
    const { t } = useTranslation('common');
    const { showToast } = useToast();

    const [keys, setKeys] = useState<FreeKeyPublic[]>([]);
    const [loading, setLoading] = useState(false);
    const [periodType, setPeriodType] = useState<PeriodType>('month');
    const [periodValue, setPeriodValue] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [canViewFull, setCanViewFull] = useState(false);
    const [message, setMessage] = useState<string>('');

    // Initialize defaults
    useEffect(() => {
        if (!selectedDate) {
            const today = getVietnamToday();
            setSelectedDate(today);
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            setPeriodValue(`${year}-${month}`);
        }
    }, [selectedDate]);

    // Handle period type change
    useEffect(() => {
        if (periodValue) {
            const date = periodValueToDate(periodValue, periodType);
            if (date) {
                setSelectedDate(date);
                // Re-calculate value to ensure format matches new type
                const newValue = dateToPeriodValue(date, periodType);
                if (newValue !== periodValue) {
                    setPeriodValue(newValue);
                }
            }
        } else {
            const today = getVietnamToday();
            setSelectedDate(today);
            setPeriodValue(dateToPeriodValue(today, periodType));
        }
    }, [periodType]);

    // Fetch keys when periodValue changes
    useEffect(() => {
        if (periodValue) {
            fetchKeys();
        }
    }, [periodValue, periodType]);

    const fetchKeys = async () => {
        if (!periodValue) return;

        setLoading(true);
        try {
            const data = await freeKeyService.getKeysByPeriod(periodType, periodValue);
            setKeys(data.keys);
            setCanViewFull(data.can_view_full);
            setMessage(data.message || '');
        } catch (error: any) {
            showToast(error.response?.data?.error || t('checkView.messages.error'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (date: Date | null) => {
        if (date) {
            setSelectedDate(date);
            const newPeriodValue = dateToPeriodValue(date, periodType);
            setPeriodValue(newPeriodValue);
        }
    };

    return {
        keys,
        loading,
        periodType,
        setPeriodType,
        periodValue,
        setPeriodValue,
        selectedDate,
        handleDateChange,
        canViewFull,
        message,
        refresh: fetchKeys
    };
};
